// Empire Apps Chassis · elder · a11y
// WCAG-AAA-aware utilities for elder-first UIs.
// Pure functions where possible; the side-effect helpers live at the bottom.

import type {
  ContrastResult,
  FontPreference,
  FontScale,
  AnnounceOptions,
  FocusTrapCleanup,
} from "./types.ts";
import { FONT_SCALES } from "./types.ts";

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance(rgb: readonly [number, number, number]): number {
  const [r, g, b] = rgb;
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

function parseHexColor(hex: string): readonly [number, number, number] | null {
  const h = hex.replace(/^#/, "");
  if (h.length !== 6 && h.length !== 3) return null;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return [r, g, b] as const;
}

function parseRgbColor(value: string): readonly [number, number, number] | null {
  const match = value.match(/rgba?\s*\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  if (!match) return null;
  const r = Number(match[1]);
  const g = Number(match[2]);
  const b = Number(match[3]);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return [r, g, b] as const;
}

function parseColor(value: string): readonly [number, number, number] | null {
  return parseHexColor(value) ?? parseRgbColor(value);
}

function contrastRatio(
  fg: readonly [number, number, number],
  bg: readonly [number, number, number],
): number {
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

function suggestFix(
  fg: readonly [number, number, number],
  bg: readonly [number, number, number],
  targetRatio: number,
): string {
  // Aim to darken or lighten foreground to meet target ratio. Coarse pass.
  const bgLum = relativeLuminance(bg);
  // Decide whether fg should be lighter or darker
  const wantLighter = bgLum < 0.5;
  const steps = 16;
  let best: readonly [number, number, number] = fg;
  let bestRatio = contrastRatio(fg, bg);
  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    const target = wantLighter ? 255 : 0;
    const adjusted: readonly [number, number, number] = [
      Math.round(fg[0] + (target - fg[0]) * t),
      Math.round(fg[1] + (target - fg[1]) * t),
      Math.round(fg[2] + (target - fg[2]) * t),
    ];
    const r = contrastRatio(adjusted, bg);
    if (r >= targetRatio) {
      best = adjusted;
      bestRatio = r;
      break;
    }
    if (r > bestRatio) {
      best = adjusted;
      bestRatio = r;
    }
  }
  const hex = "#" +
    best.map((n) => n.toString(16).padStart(2, "0")).join("");
  return `try fg ${hex} (ratio ${bestRatio.toFixed(2)})`;
}

export function enforceContrast(fg: string, bg: string): ContrastResult {
  const parsedFg = parseColor(fg);
  const parsedBg = parseColor(bg);
  if (!parsedFg || !parsedBg) {
    return {
      ratio: 0,
      passesAALarge: false,
      passesAANormal: false,
      passesAAALarge: false,
      passesAAANormal: false,
      suggestedFix: "invalid color input",
    };
  }
  const ratio = contrastRatio(parsedFg, parsedBg);
  const passesAANormal = ratio >= 4.5;
  const passesAAANormal = ratio >= 7.0;
  return {
    ratio,
    passesAALarge: ratio >= 3.0,
    passesAANormal,
    passesAAALarge: ratio >= 4.5,
    passesAAANormal,
    suggestedFix: passesAAANormal
      ? null
      : suggestFix(parsedFg, parsedBg, 7.0),
  };
}

const MIN_TAP_POINTS = 88;
const POINT_TO_PX = 1.333; // 1pt = ~1.333px at 96dpi

export function ensureMinTapSize(el: HTMLElement, minPoints: number = MIN_TAP_POINTS): void {
  const minPx = Math.ceil(minPoints * POINT_TO_PX);
  const rect = el.getBoundingClientRect();
  if (rect.width < minPx) {
    el.style.minWidth = `${minPx}px`;
  }
  if (rect.height < minPx) {
    el.style.minHeight = `${minPx}px`;
  }
  // Add tap-target outline for debug audit
  el.dataset["minTap"] = String(minPoints);
}

let liveRegion: HTMLDivElement | null = null;
function ensureLiveRegion(polite: boolean): HTMLDivElement {
  const id = polite ? "empire-live-polite" : "empire-live-assertive";
  let region = document.getElementById(id) as HTMLDivElement | null;
  if (region) return region;
  region = document.createElement("div");
  region.id = id;
  region.setAttribute("role", "status");
  region.setAttribute("aria-live", polite ? "polite" : "assertive");
  region.setAttribute("aria-atomic", "true");
  region.style.position = "absolute";
  region.style.width = "1px";
  region.style.height = "1px";
  region.style.overflow = "hidden";
  region.style.clip = "rect(0 0 0 0)";
  region.style.clipPath = "inset(50%)";
  region.style.margin = "-1px";
  region.style.padding = "0";
  region.style.border = "0";
  region.style.whiteSpace = "nowrap";
  document.body.appendChild(region);
  if (polite) liveRegion = region;
  return region;
}

export function screenReaderAnnounce(
  msg: string,
  options: Partial<AnnounceOptions> = {},
): void {
  const polite = options.polite ?? true;
  const clearAfterMs = options.clearAfterMs ?? 1500;
  const region = ensureLiveRegion(polite);
  region.textContent = "";
  // Force re-announce by re-setting on next tick
  requestAnimationFrame(() => {
    region.textContent = msg;
    if (clearAfterMs > 0) {
      window.setTimeout(() => {
        if (region.textContent === msg) region.textContent = "";
      }, clearAfterMs);
    }
  });
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"]), audio[controls], video[controls], iframe, [contenteditable="true"]';

export function focusTrap(container: HTMLElement): FocusTrapCleanup {
  const previousActive = document.activeElement as HTMLElement | null;
  const focusables = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
  const first = focusables[0];
  const last = focusables[focusables.length - 1];

  function onKeyDown(ev: KeyboardEvent): void {
    if (ev.key !== "Tab") return;
    if (focusables.length === 0) {
      ev.preventDefault();
      return;
    }
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last?.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first?.focus();
    }
  }

  container.addEventListener("keydown", onKeyDown);
  first?.focus();

  return {
    release: () => {
      container.removeEventListener("keydown", onKeyDown);
      if (previousActive && typeof previousActive.focus === "function") {
        previousActive.focus();
      }
    },
  };
}

export function getOSReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function prefersIncreasedFontSize(): FontPreference {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return { scale: 1.0, source: "default" };
  }
  const userPref = localStorage.getItem("empire.elder.fontScale");
  if (userPref) {
    const parsed = Number(userPref) as FontScale;
    if ((FONT_SCALES as readonly number[]).includes(parsed)) {
      return { scale: parsed, source: "user-explicit" };
    }
  }
  // CSS prefers-contrast as a proxy. iOS surfaces Dynamic Type via custom queries
  if (window.matchMedia("(prefers-contrast: more)").matches) {
    return { scale: 1.5, source: "os-prefers" };
  }
  // Heuristic: rootFont >= 18px implies elder mode
  const computed = getComputedStyle(document.documentElement);
  const rootSize = parseFloat(computed.fontSize);
  if (Number.isFinite(rootSize) && rootSize >= 22) return { scale: 1.5, source: "os-prefers" };
  if (Number.isFinite(rootSize) && rootSize >= 19) return { scale: 1.25, source: "os-prefers" };
  return { scale: 1.0, source: "default" };
}

export function setFontScale(scale: FontScale): void {
  localStorage.setItem("empire.elder.fontScale", String(scale));
  document.documentElement.style.setProperty("--empire-elder-font-scale", String(scale));
  screenReaderAnnounce(
    `Font size set to ${scale === 1.0 ? "normal" : scale === 1.25 ? "large" : scale === 1.5 ? "extra large" : "biggest"}`,
  );
}

export function getRecommendedFontPx(scale: FontScale, basePx: number): number {
  return Math.round(basePx * scale);
}
